import type { IInlineBoxData } from '$/components/InlineBox/types';

export const POST_TASK_INLINE_TAG = 'post_task';

export const POST_TASK_COMPANION_INLINE_TAG = 'target_region_style';

/**
 * 后链路标签后的专用光标锚点。
 * 使用有实际排版宽度的窄空格，避免 WebKit 将零宽文本后的 caret 错画到标签左侧。
 */
export const POST_TASK_CARET_ANCHOR = '\u200A';

/** 判断富文本行内数据是否为图片输入框专用的后链路能力标签。 */
export function isPostTaskInlineBoxData(
  data: Pick<IInlineBoxData, 'tag'> | null | undefined,
) {
  return data?.tag === POST_TASK_INLINE_TAG;
}

/** 判断行内数据是否为紧随 post_task 展示的附属标签。 */
export function isPostTaskCompanionInlineBoxData(
  data: Pick<IInlineBoxData, 'tag'> | null | undefined,
) {
  return data?.tag === POST_TASK_COMPANION_INLINE_TAG;
}

/** 判断两个连续标签之间的文本是否仅承担间隔与光标锚点作用。 */
export function isPostTaskPrefixSeparatorText(text: string) {
  return /^[\u200A\u200B \t]*$/.test(text);
}

/** 把标签后的旧零宽锚点或提示词分隔空格收敛成专用窄空格锚点。 */
export function normalizePostTaskCaretText(text: string) {
  const withoutLegacyAnchor = text.replace(/^\u200B+/, '');
  if (withoutLegacyAnchor.startsWith(POST_TASK_CARET_ANCHOR)) {
    return withoutLegacyAnchor;
  }
  if (withoutLegacyAnchor.startsWith(' ')) {
    return `${POST_TASK_CARET_ANCHOR}${withoutLegacyAnchor.slice(1)}`;
  }
  return `${POST_TASK_CARET_ANCHOR}${withoutLegacyAnchor}`;
}

/** 把标签前的光标锚点收敛到文本末尾，保证标签左右都存在可编辑落点。 */
export function normalizePostTaskLeadingCaretText(text: string) {
  const content = text.replace(
    new RegExp(`[\\u200B${POST_TASK_CARET_ANCHOR}]`, 'g'),
    '',
  );
  return `${content}${POST_TASK_CARET_ANCHOR}`;
}

/**
 * 序列化标签后的文本：空锚点不落业务值，有用户文本时恢复为一个普通分隔空格。
 */
export function serializePostTaskCaretText(text: string) {
  const withoutLegacyAnchor = text.replace(/^\u200B+/, '');
  if (!withoutLegacyAnchor.startsWith(POST_TASK_CARET_ANCHOR)) {
    return withoutLegacyAnchor;
  }
  const content = withoutLegacyAnchor.slice(POST_TASK_CARET_ANCHOR.length);
  return content ? ` ${content}` : '';
}

/** 序列化标签前的文本时移除内部光标锚点，保留用户真实输入内容。 */
export function serializePostTaskLeadingCaretText(text: string) {
  return text.replace(
    new RegExp(`[\\u200B${POST_TASK_CARET_ANCHOR}]`, 'g'),
    '',
  );
}

/** 删除标签时合并前后文本，并把仅用于标签分隔的空白收敛成一个普通空格。 */
export function mergePostTaskAdjacentText(leadingText: string, trailingText: string) {
  const leadingContent = serializePostTaskLeadingCaretText(leadingText).replace(/[ \t]+$/, '');
  const trailingContent = serializePostTaskCaretText(trailingText).replace(/^[ \t]+/, '');
  if (!leadingContent) return trailingContent;
  if (!trailingContent) return leadingContent;
  return `${leadingContent} ${trailingContent}`;
}

/** 跳过标签后文本节点内部的专用或旧版锚点，返回真实可见光标起点。 */
export function getPostTaskCaretTextOffset(text: string) {
  let offset = 0;
  while (text[offset] === '\u200B' || text[offset] === POST_TASK_CARET_ANCHOR) {
    offset += 1;
  }
  return offset;
}
